<template>
  <div>
    <v-data-table
      class="q-marketing-resources-table"
      :headers="[
        { text: 'Resource', value: 'DocTitle', width: '100%' },
        { text: 'Updated Date', value: 'LastChangeDate', width: '150px' },
        { text: 'Audience', value: 'Audience', align: 'center', width: '150px' },
        { text: 'Type', value: 'type', width: '100px' },
        { text: '', value: 'download_url', sortable: false },
      ]"
      v-model="mSelected"
      :items="items"
      :expanded.sync="expanded"
      :mobile-breakpoint="0"
      item-key="ID"
      no-data-text="No documents found"
      show-select
    >
      <template #expanded-item="{ item }">
        <td colspan="100">
          <div class="q-marketing-resources-table__description q-marketing-resources-table-description">
            <div class="q-marketing-resources-table-description__header">
              <p class="q-marketing-resources-table-description__label">Description</p>
            </div>
            <p class=" mb-0">{{ item.DocDesc }}</p>
          </div>
        </td>
      </template>

      <template #item.Audience="{ item }">
        <v-chip :color="item.Audience === 'Agent' ? '#0083EB' : '#46C3B2'" dark>
          {{ item.Audience }}
        </v-chip>
      </template>

      <template #item.type="{ item }">
        <div class="q-marketing-resources-table__type-column">
          <v-icon>
            {{ getTypeIconName(item.DocType) }}
          </v-icon>

          {{ item.DocType }}
        </div>
      </template>

      <template #item.download_url="bind">
        <div class="d-flex justify-end align-center q-marketing-resources-table__actions">
          <v-btn text small @click.stop="previewDocument(bind.item)">
            <v-icon small>fas fa-eye</v-icon>
          </v-btn>
          <v-btn :href="bind.item.DownloadLink" target="_blank" text small>
            <v-icon small>fas fa-download</v-icon>
          </v-btn>
          <v-btn small fab icon @click.stop v-clipboard:copy="bind.item.ViewLink" v-clipboard:success="handleCopyStatus">
              <v-icon>fas fa-copy</v-icon>
          </v-btn>
          <v-btn text small @click="toggleExpand(bind.item)">
            <v-icon>fas fa-chevron-down</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="showPreview" width="95%" max-width="900px" :fullscreen="$vuetify.breakpoint.xs">
      <v-card
        :key="previewDocumentUrl"
        style="position:relative; min-height:10rem;"
        :style="previewDocumentDocType === 'office' && 'overflow:hidden'"
      >
        <v-btn @click="closePreview" fab color="red" right absolute>
          <v-icon>fas fa-times-circle</v-icon>
        </v-btn>
        <PdfPreview v-if="previewDocumentDocType === 'pdf'" :value="previewDocumentUrl" />
        <v-img v-else-if="previewDocumentDocType === 'image'" :src="previewDocumentUrl" />
        <VueDocPreview v-else :url="previewDocumentUrl" :type="previewDocumentDocType" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import VueDocPreview from 'vue-doc-preview'
import PdfPreview from '@/CMS/editor/components/PdfPreview.vue'

const previewTypeMap = {
    office: ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'],
    image: ['jpg', 'png', 'gif'],
    pdf: ['pdf'],
    code: ['csv'],
    text: ['text'],
}

export default {
  name: "MarketingResourceTable",

	components: {
		VueDocPreview,
		PdfPreview,
	},

  props: {
    items: { type: Array },
    selected: { type: Array, default: () => [] },
  },

  data() {
    return {
      expanded: [],
			showPreview: false,
      previewDocumentDocType: null,
      previewDocumentUrl: null,
    };
  },

  computed: {
    mSelected: {
      get () { return this.selected },
      set (v) { this.$emit('update:selected', v) },
    },
  },

	watch: {
		showPreview (val) {
      if (!val) { this.previewDocumentUrl = '' }
    },
	},

  methods: {
    previewDocument (doc) {
			this.previewDocumentDocType = this.getPreviewType(doc.DocType)
      this.previewDocumentUrl = this.getDocLink(doc.ViewLink, doc.DocType)
			this.showPreview = true
    },
    getPreviewType (doctype) {
			const type = doctype.toLowerCase?.() || doctype
			const previewTypeName = Object.entries(previewTypeMap).find(([name , types]) => types.includes(type))
			return previewTypeName?.[0] || 'text'
    },
		getDocLink (link, docType) {
      if (link.startsWith('http://localhost:8000')) {
        return link.replace('http://localhost:8000', process.env.VUE_APP_BASE_URL)
      }
			if (process.env.NODE_ENV === 'development') { // to avoid CORS errors in dev
				const previewType = this.getPreviewType(docType)
				if (previewType !== 'office') {
					return link.replace(new URL(link).origin, '')
				}
			}
			return link
		},
    getTypeIconName(type) {
      type = type.toLowerCase?.() || type;

      if (type === 'pdf') {
        return 'fas fa-file-pdf';
      }

      if (type === 'doc' || type === 'docx') {
        return 'fas fa-file-word';
      }

      if (type === 'xls' || type === 'xlsx') {
        return 'fas fa-file-excel';
      }

      if (type === 'ppt' || type === 'pptx') {
        return 'fas fa-file-powerpoint';
      }

      if (type === 'zip') {
        return 'fas fa-file-archive';
      }

      if (type === 'jpg' || type === 'jpeg' || type === 'png') {
        return 'fas fa-file-image';
      }

      if (type === 'mp4' || type === 'mov' || type === 'avi') {
        return 'fas fa-file-video';
      }

      if (type === 'mp3' || type === 'wav') {
        return 'fas fa-file-audio';
      }

      return 'fas fa-file';
    },
    toggleExpand(item) {
      if (this.expanded.includes(item)) {
        this.expanded = this.expanded.filter((i) => i !== item);
      } else {
        this.expanded.push(item);
      }
    },
    handleCopyStatus(status) {
      this.showInfo("Document URL copied to clipboard!")
    },
    closePreview () {
      this.previewDocumentUrl = ''
			this.showPreview = false
    },
  },
};
</script>

<style lang="scss" scoped>
  .q-marketing-resources-table {
    width: 100%;

    &__type-column {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    &__description {
      padding: 1rem;
    }

    &__actions {
      .v-icon {
        opacity: 0.5;
        transition: opacity 0.2s ease-in-out;

        &:hover {
          opacity: 0.7;
        }
      }
    }

    ::v-deep .v-data-table__checkbox.v-simple-checkbox {
      .v-icon:not(.fa-square) {
        color: var(--primary) !important;
      }
    }
  }

  .q-marketing-resources-table-description {
    width: 100%;
    
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      align-items: center;
      margin-bottom: 0.5rem;
      width: 100%;
    }

    &__label, &__updated {
      font-size: 0.75rem;
      margin: 0;
    }

    &__label {
      font-weight: bold;
    }
  }
</style>